import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Row, Col, Panel, Button, Label } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';

// Triage tabs for the doctor-reports view. `all` clears the status filter.
const STATUS_TABS = [
    { key: 'all', label: 'Tous' },
    { key: 'pending', label: 'En attente' },
    { key: 'reviewed', label: 'Traités' },
    { key: 'dismissed', label: 'Rejetés' }
];

const STATUS_META = {
    pending: { text: 'En attente', style: 'warning' },
    reviewed: { text: 'Traité', style: 'success' },
    dismissed: { text: 'Rejeté', style: 'default' }
};

const fullName = (p) => {
    if (!p) return null;
    const name = `${p.FName || ''} ${p.LName || ''}`.trim();
    return name || p.email || null;
};

// Friendly French labels for consultation type slugs. Unknown slugs fall back to
// a title-cased version ("some-slug" -> "Some slug") so nothing shows a raw dash.
const TYPE_LABELS = {
    generaliste: 'Généraliste',
    'medecine-generale': 'Généraliste',
    dermatologie: 'Dermatologie',
    gynecologie: 'Gynécologie',
    psychiatre: 'Psychiatrie',
    psychologie: 'Psychologie',
    pediatrie: 'Pédiatrie',
    'ordonnance-en-ligne': 'Ordonnance en ligne',
    'traitement-anti-obesite': 'Anti-obésité',
    'evaluation-nutritionnelle': 'Nutrition'
};

const typeLabel = (t) => {
    if (!t) return 'Consultation';
    if (TYPE_LABELS[t]) return TYPE_LABELS[t];
    return t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, ' ');
};

const Pager = ({ page, totalPage, setPage }) => {
    if (totalPage <= 1) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 10 }}>
            <Button bsSize="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Précédent
            </Button>
            <span>Page <strong>{page}</strong> / {totalPage}</span>
            <Button bsSize="small" disabled={page >= totalPage} onClick={() => setPage(page + 1)}>
                Suivant
            </Button>
        </div>
    );
};

// --- Médecins signalés par des patients (collection DoctorReport) ---
const MedecinsSignales = () => {
    const [reports, setReports] = useState([]);
    const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, reviewed: 0, dismissed: 0 });
    const [tab, setTab] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);

    const fetchReports = useCallback(() => {
        setLoading(true);
        const statusParam = tab === 'all' ? '' : `&status=${tab}`;
        fetchApi(`/signalements?page=${page}${statusParam}`)
            .then((res) => {
                setReports(res.reports || []);
                setTotalPage(res.countPages || 0);
                setStatusCounts(res.statusCounts || { all: 0, pending: 0, reviewed: 0, dismissed: 0 });
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification('Erreur', 'Impossible de charger les signalements : ' + err, 'warning');
            });
    }, [page, tab]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const selectTab = (key) => {
        setTab(key);
        setPage(1);
    };

    const changeStatus = (report, status) => {
        if (report.status === status) return;
        setUpdatingId(report._id);
        fetchApi(`/signalements/${report._id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        })
            .then(() => {
                setUpdatingId(null);
                notif.addNotification('Succès', 'Statut du signalement mis à jour', 'success');
                fetchReports();
            })
            .catch((err) => {
                setUpdatingId(null);
                notif.addNotification('Erreur', 'Mise à jour impossible : ' + err, 'warning');
            });
    };

    return (
        <Row>
            <Col xs={12}>
                <p className="text-muted" style={{ marginTop: 12 }}>
                    Médecins signalés par des patients depuis leur consultation. Chaque
                    signalement précise le médecin concerné, le patient, la consultation
                    d'origine et le motif.
                </p>

                <div style={{ marginBottom: 15 }}>
                    {STATUS_TABS.map((t) => (
                        <Button
                            key={t.key}
                            bsStyle={tab === t.key ? 'primary' : 'default'}
                            style={{ marginRight: 8 }}
                            onClick={() => selectTab(t.key)}
                        >
                            {t.label}{' '}
                            <Label bsStyle={tab === t.key ? 'default' : 'primary'}>
                                {statusCounts[t.key] || 0}
                            </Label>
                        </Button>
                    ))}
                    <Button bsStyle="link" onClick={fetchReports} disabled={loading}>
                        {loading ? 'Chargement...' : 'Actualiser'}
                    </Button>
                </div>

                <Panel>
                    <Panel.Heading>
                        <h4>Liste ({statusCounts[tab] || 0})</h4>
                    </Panel.Heading>
                    <Panel.Body style={{ overflowX: 'auto' }}>
                        {loading && <p className="text-muted">Chargement...</p>}
                        {!loading && reports.length === 0 && (
                            <p className="text-muted">Aucun signalement.</p>
                        )}
                        {!loading && reports.length > 0 && (
                            <table className="table table-bordered table-hover table-condensed">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Médecin signalé</th>
                                        <th>Patient</th>
                                        <th>Consultation</th>
                                        <th>Motif</th>
                                        <th>Statut</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((r) => {
                                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                                        const doctor = r.doctorId;
                                        const patient = r.patientId;
                                        const busy = updatingId === r._id;
                                        return (
                                            <tr key={r._id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {dateFormat(r.createdAt, 'dd/mm/yyyy HH:MM')}
                                                </td>
                                                <td>
                                                    {doctor ? (
                                                        <Link to={`/doctors/detail/${doctor._id}`}>
                                                            {fullName(doctor) || 'Médecin'}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                    {doctor && doctor.email && (
                                                        <div><small className="text-muted">{doctor.email}</small></div>
                                                    )}
                                                </td>
                                                <td>
                                                    {patient ? (
                                                        <Link to={`/users/detail/${patient._id}`}>
                                                            {fullName(patient) || 'Patient'}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {r.requestId ? (
                                                        <Link to={`/requests/detail/${r.requestId._id}`}>
                                                            <Label bsStyle="info">{typeLabel(r.requestId.type)}</Label>
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>{r.reason || <span className="text-muted">—</span>}</td>
                                                <td><Label bsStyle={meta.style}>{meta.text}</Label></td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {r.status !== 'reviewed' && (
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle="success"
                                                            style={{ marginRight: 6 }}
                                                            disabled={busy}
                                                            onClick={() => changeStatus(r, 'reviewed')}
                                                        >
                                                            Marquer traité
                                                        </Button>
                                                    )}
                                                    {r.status !== 'dismissed' && (
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle="default"
                                                            style={{ marginRight: 6 }}
                                                            disabled={busy}
                                                            onClick={() => changeStatus(r, 'dismissed')}
                                                        >
                                                            Rejeter
                                                        </Button>
                                                    )}
                                                    {r.status !== 'pending' && (
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle="warning"
                                                            disabled={busy}
                                                            onClick={() => changeStatus(r, 'pending')}
                                                        >
                                                            Rouvrir
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                        <Pager page={page} totalPage={totalPage} setPage={setPage} />
                    </Panel.Body>
                </Panel>
            </Col>
        </Row>
    );
};

// --- Patients signalés « à risque » par des médecins (Request.isSignaled) ---
const PatientsSignales = () => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchList = useCallback(() => {
        setLoading(true);
        fetchApi(`/signalements/patients?page=${page}`)
            .then((res) => {
                setRows(res.patients || []);
                setTotalPage(res.countPages || 0);
                setCount(res.count || 0);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification('Erreur', 'Impossible de charger les patients signalés : ' + err, 'warning');
            });
    }, [page]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    return (
        <Row>
            <Col xs={12}>
                <p className="text-muted" style={{ marginTop: 12 }}>
                    Patients signalés « à risque » par un médecin lors d'une consultation.
                    Le signalement précise le patient concerné, le médecin qui l'a signalé,
                    la consultation et le motif.
                </p>

                <div style={{ marginBottom: 15 }}>
                    <Button bsStyle="link" style={{ paddingLeft: 0 }} onClick={fetchList} disabled={loading}>
                        {loading ? 'Chargement...' : 'Actualiser'}
                    </Button>
                </div>

                <Panel>
                    <Panel.Heading>
                        <h4>Liste ({count})</h4>
                    </Panel.Heading>
                    <Panel.Body style={{ overflowX: 'auto' }}>
                        {loading && <p className="text-muted">Chargement...</p>}
                        {!loading && rows.length === 0 && (
                            <p className="text-muted">Aucun patient signalé.</p>
                        )}
                        {!loading && rows.length > 0 && (
                            <table className="table table-bordered table-hover table-condensed">
                                <thead>
                                    <tr>
                                        <th>Date consultation</th>
                                        <th>Patient signalé</th>
                                        <th>Signalé par (médecin)</th>
                                        <th>Consultation</th>
                                        <th>Motif</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => {
                                        const patient = r.userId;
                                        const doctor = r.doctorId;
                                        return (
                                            <tr key={r._id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {dateFormat(r.createdAt, 'dd/mm/yyyy HH:MM')}
                                                </td>
                                                <td>
                                                    {patient ? (
                                                        <Link to={`/users/detail/${patient._id}`}>
                                                            {fullName(patient) || 'Patient'}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                    {patient && patient.email && (
                                                        <div><small className="text-muted">{patient.email}</small></div>
                                                    )}
                                                </td>
                                                <td>
                                                    {doctor ? (
                                                        <Link to={`/doctors/detail/${doctor._id}`}>
                                                            {fullName(doctor) || 'Médecin'}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Link to={`/requests/detail/${r._id}`}>
                                                        <Label bsStyle="info">{typeLabel(r.type)}</Label>
                                                    </Link>
                                                </td>
                                                <td>{r.signalReason || <span className="text-muted">—</span>}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                        <Pager page={page} totalPage={totalPage} setPage={setPage} />
                    </Panel.Body>
                </Panel>
            </Col>
        </Row>
    );
};

const Signalements = () => {
    const [mode, setMode] = useState('doctors');

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Signalements</h2>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col xs={12}>
                    <Button
                        bsStyle={mode === 'doctors' ? 'primary' : 'default'}
                        style={{ marginRight: 8 }}
                        onClick={() => setMode('doctors')}
                    >
                        Médecins signalés
                    </Button>
                    <Button
                        bsStyle={mode === 'patients' ? 'primary' : 'default'}
                        onClick={() => setMode('patients')}
                    >
                        Patients signalés
                    </Button>
                </Col>
            </Row>

            {mode === 'doctors' ? <MedecinsSignales /> : <PatientsSignales />}
        </Grid>
    );
};

export default Signalements;
